import axios from "axios";
import { createActions, createReducer } from "reduxsauce";
import { urls } from "../../api/urls";
import { apiConfig } from "../../api/config";
import { createUser, createProfile } from "../dataMap/user";
import { NotificationManager } from "react-notifications";

/**Actions and types definitions **/
export const { Types, Creators } = createActions({
  setUser: ["user"],
  setUserFailure: ["error"],
  setAccess: ["access"],
  passwordChange: null,
  profileChange: ["user"],
  registerSuccess: ["user"],
  registerFailure: ["error"],
});

interface IUser {
  userName: string;
  email: string;
}

interface IUserState {
  user: IUser | null;
  access: string;
  error: string;
  status: string;
}

const INITIAL_STATE: IUserState = {
  user: null,
  access: "",
  error: "",
  status: "",
};

export const getUser = (
  state = INITIAL_STATE,
  action: { user: IUser }
): IUserState => {
  return {
    ...state,
    user: action.user,
    error: "",
  };
};

export const setAccess = (
  state = INITIAL_STATE,
  action: { access: string }
): IUserState => {
  return {
    ...state,
    access: action.access,
    error: "",
  };
};

export const failure = (
  state = INITIAL_STATE,
  action: { error: string }
): IUserState => {
  return {
    ...state,
    error: action.error,
    status: "",
  };
};

export const newPassword = (state = INITIAL_STATE): IUserState => {
  return {
    ...state,
    error: "",
    status: "password-saved",
  };
};

export const newProfile = (
  state = INITIAL_STATE,
  action: { user: IUser }
): IUserState => {
  return {
    ...state,
    error: "",
    status: "profile-saved",
    user: { ...state.user, ...action.user },
  };
};

export const registerSuccess = (
  state = INITIAL_STATE,
  action: { error: string }
): IUserState => {
  return {
    ...state,
    status: "created",
    error: "",
  };
};

export const registerFailure = (
  state = INITIAL_STATE,
  action: { error: string }
): IUserState => {
  return {
    ...state,
    status: "",
    error: action.error,
  };
};

export const HANDLERS = {
  [Types.SET_USER]: getUser,
  [Types.SET_USER_FAILURE]: failure,
  [Types.SET_ACCESS]: setAccess,
  [Types.PASSWORD_CHANGE]: newPassword,
  [Types.PROFILE_CHANGE]: newProfile,
  [Types.REGISTER_SUCCESS]: registerSuccess,
  [Types.REGISTER_FAILURE]: registerFailure,
};
export default createReducer(INITIAL_STATE, HANDLERS);

export const Actions = {
  ...Creators,

  getToken: async function (
    dispatch,
    credentials: { username: string; password: string }
  ) {
    let result = false;
    await axios
      .post(urls.TOKEN, credentials)
      .then((response) => {
        this.currentUser(dispatch);
        localStorage.setItem("access", response.data.access);
        localStorage.setItem("refresh", response.data.refresh);
        dispatch(Creators.setAccess(response.data.access));
        result = true;
      })
      .catch((error) => {
        //   alert("Error logging in" + error);
        dispatch(
          Creators.setUserFailure(
            "No active account found with the given credentials"
          )
        );

        throw error;
      });
    return result;
  },

  getTokenGoogle: async function (
    dispatch,
    accessToken
  ) {
    let result = false;
    await axios
      .post(urls.TOKEN_GOOGLE, { access_token: accessToken })
      .then((response) => {
        this.currentUser(dispatch);
        localStorage.setItem("access", response.data.access);
        localStorage.setItem("refresh", response.data.refresh);
        dispatch(Creators.setAccess(response.data.access));
        result = true;
      })
      .catch((error) => {
        dispatch(
          Creators.setUserFailure(
            "Failed to create new / find existing user with Google"
          )
        );
        throw error;
      });
    return result;
  },

  currentUser: (dispatch) => {
    apiConfig
      .get(urls.USER)
      .then(async (user) => {
        // const profile = await apiConfig.get(urls.PROFILE);
        dispatch(Creators.setUser(createUser(user.data, user.data.profile)));
      })
      .catch((error) => {
        //   alert('currentUser' + error)
        dispatch(Creators.setUserFailure(error));
        // throw "No current user";
      });
  },

  register: async function (dispatch, signupData) {
    let newUser = {};
    var userForm = new FormData();

    for (var key in signupData) {
      if (key === "profile") {
        userForm.append(key, JSON.stringify(signupData.profile));
      } else {
        userForm.append(key, signupData[key]);
      }
    }

    await axios({
      method: "post",
      url: urls.USERS,
      headers: { "content-type": "multipart/form-data" },
      data: userForm,
    })
      .then((response) => {
        newUser = response.data;
        dispatch(Creators.registerSuccess(newUser));
      })
      .catch((error) => {
        const err_obj = error.response.data;
        dispatch(Creators.registerFailure(err_obj[Object.keys(err_obj)[0]][0]));
      });
    return newUser;
  },

  getNewToken: async function (refreshToken: string | null) {
    let data = {
      refresh: refreshToken,
    };
    await apiConfig
      .post(urls.TOKEN_REFRESH, data)
      .then((response) => {
        localStorage.setItem("access", response.data.access);
      })
      .catch((error) => {
        //   alert("Error getting new token: " + error);
        throw error;
      });
  },

  changePassword: (dispatch, oldPassword: string, newPassword: string) => {
    apiConfig
      .put(urls.CHANGE_PASSWORD, {
        old_password: oldPassword,
        new_password: newPassword,
      })
      .then(() => {
        dispatch(Creators.passwordChange());
        NotificationManager.info("Password changed!", "New Password");
      })
      .catch((error) => {
        let errMsg = "Invalid Password";
        if (
          error.response &&
          error.response.data &&
          error.response.data.new_password
        )
          errMsg = error.response.data.new_password[0];
        if (
          error.response &&
          error.response.data &&
          error.response.data.old_password
        )
          errMsg = error.response.data.old_password[0];
        NotificationManager.error(errMsg, "New Password");
      });
  },

  changeProfile: (
    dispatch,
    profile: { first_name: string; last_name: string; image?: File }
  ) => {
    const formData = new FormData();
    if (profile.image) {
      formData.append("image", profile.image, profile.image.name);
    }
    formData.append("first_name", profile.first_name);
    formData.append("last_name", profile.last_name);
    apiConfig
      .put(urls.PROFILE, formData)
      .then((user) => {
        dispatch(Creators.profileChange(createProfile(user.data)));
        NotificationManager.info(
          "Profile changed!",
          "Persönliche Einstellungen"
        );
      })
      .catch((error) => {
        let errMsg = "Invalid Password";
        if (
          error.response &&
          error.response.data &&
          error.response.data.first_name
        )
          errMsg = error.response.data.first_name[0];
        if (
          error.response &&
          error.response.data &&
          error.response.data.last_name
        )
          errMsg = error.response.data.last_name[0];
        NotificationManager.error(errMsg, "Persönliche Einstellungen");
      });
  },

};
